import Dialog from "../../General/ConfirmableDialog";
import { useQueryClient } from "@tanstack/react-query";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useState } from "react";
import axios from "axios";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";

interface UnassignButtonProps {
  id: string;
  type: "driver" | "trailer" | "truck" | "dispatcher";
  truckId?: string;
}

const UnassignButton = ({ id, type, truckId }: UnassignButtonProps) => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [open, setOpen] = useState(false);
  const handleSnackbarClose = () => setStatus("idle");

  const handleUnassign = async () => {
    setLoading(true);
    try {
      let url = "";

      switch (type) {
        case "driver":
          if (!truckId) {
            throw new Error("Truck ID is required to unassign a driver");
          }
          url = `${apiVariable}/unassignDriver/${id}/${truckId}`;
          break;
        case "trailer":
          url = `${apiVariable}/unassignTrailer/${id}`;
          break;
        case "truck":
          url = `${apiVariable}/unassignTruck/${id}`;
          break;
        case "dispatcher":
          url = `${apiVariable}/unassignDispatcher/${id}`;
          break;
        default:
          throw new Error("Invalid type for unassignment");
      }

      await axios.put(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      queryClient.invalidateQueries({
        queryKey: [
          type === "driver"
            ? "assignedDrivers"
            : type === "trailer"
            ? "assignedTrailers"
            : "assignedTrucks",
        ],
      });
      type === "dispatcher" &&
        queryClient.invalidateQueries({
          queryKey: ["assignedDispatchers", id],
        });

      type === "dispatcher" &&
        queryClient.invalidateQueries({
          queryKey: ["drivers", true],
        });
      setStatus("success");
    } catch (error: any) {
      setError(error.response?.data || `Failed to unassign ${type}`);
      setStatus("error");
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        color="error"
        onClick={(event) => {
          setOpen(true);
          event.stopPropagation();
        }}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          `Unassign ${type.charAt(0).toUpperCase() + type.slice(1)}`
        )}
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <Box sx={{ p: 3 }}>
          <Typography>
            Are you sure you want to unassign this {type}?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
          >
            <Button onClick={handleUnassign} color="error">
              Yes
            </Button>
            <Button onClick={() => setOpen(false)}>No</Button>
          </Box>
        </Box>
      </Dialog>

      <StatusSnackbar
        status={status}
        successMessage={`${
          type.charAt(0).toUpperCase() + type.slice(1)
        } unassigned successfully`}
        errorMessage={error}
        handleClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default UnassignButton;
